/* eslint-disable no-use-before-define */
/* eslint-disable consistent-return */
/* eslint-disable no-shadow */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Badge,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  InputAdornment,
  Tooltip,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem as MuiMenuItem,
} from '@mui/material';
import { Send as SendIcon, People as PeopleIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import Image from '@app/components/atoms/Image';
import imageCompression from 'browser-image-compression';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
  useChat,
  useGroupChatApi,
  useTypingApi,
  useChatConnectionApi,
  useChatStatus,
} from '@app/contexts/chatContext';
import { useS3Upload } from '@app/hooks/chat/useS3Upload';
import { useUser } from '@app/contexts/userContext';
import FileInput from '@app/components/molecules/FileInput';
import MessageItem from './MessageItem';

const MAX_TOTAL_FILE_SIZE_MB = 300;
const MAX_IMAGE_SIZE_MB = 2;
const MAX_TOTAL_FILE_SIZE_BYTES = MAX_TOTAL_FILE_SIZE_MB * 1024 * 1024;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const DISALLOWED_EXTENSIONS = ['.exe', '.bat', '.cmd', '.msi'];

/**
 * EventDiscussionPanel - Real-time chat interface for event discussions
 *
 * NOTE: This component assumes that the socket connection and group chat room joining
 * are managed at the dashboard level (ViewOpportunityDashboardTemplate).
 * It focuses purely on UI and message handling.
 *
 * The parent dashboard should:
 * 1. Join the chat room when the dashboard loads
 * 2. Stay joined throughout the dashboard session
 * 3. Handle room cleanup when leaving the dashboard
 */
const EventDiscussionPanel = ({ eventTitle, group, onClose, messages }) => {
  const theme = useTheme();
  const { user: authUser } = useUser();

  const { chatState } = useChat();
  const { error } = useChatStatus();
  const { connectionStatus } = useChatConnectionApi();
  const {
    joinGroupChat,
    sendGroupMessage,
    loadGroupChatMessages,
    loadMoreGroupMessages,
  } = useGroupChatApi();
  const {
    startTyping,
    stopTyping,
  } = useTypingApi();

  const { generateAwsSignedUrl, uploadFilesToS3 } = useS3Upload();

  // Create a ref to always get the latest state
  const latestChatStateRef = useRef(chatState);
  useEffect(() => {
    latestChatStateRef.current = chatState;
  }, [chatState]);

  // Local state
  const [inputMessage, setInputMessage] = useState('');
  const [messageType, setMessageType] = useState('general');
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimer, setTypingTimer] = useState(null);
  const [showParticipants, setShowParticipants] = useState(false);

  // File upload state
  const [uploadType, setUploadType] = useState(null);
  const [filesPrepared, setFilesPrepared] = useState(false);
  const [mutationError, setMutationError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [expandedMedia, setExpandedMedia] = useState({ urls: [], index: 0 });

  // Refs for component state
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const lastMessageSentRef = useRef(0);
  const containerRef = useRef(null);

  // PAGINATION STATE
  const [allMessages, setAllMessages] = useState([]);
  const [pageInfo, setPageInfo] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const previousScrollHeightRef = useRef(0);
  const isUserScrollingRef = useRef(false);
  const hasScrolledToBottomRef = useRef(false);

  // Stable references to prevent effect re-runs
  const stableGroupId = useMemo(() => group?.id, [group?.id]);
  const stableAuthUserId = useMemo(() => authUser?.id, [authUser?.id]);

  const removeTimeStampFromFile = (fileName) => fileName && fileName?.replace(/_\d+(?=\.\w+$)/, '');

  // Auto-clear mutation error after 10 seconds
  useEffect(() => {
    if (mutationError) {
      const timer = setTimeout(() => {
        setMutationError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [mutationError]);

  // INITIAL LOAD - Load messages from backend
  useEffect(() => {
    if (stableGroupId && isInitialLoad) {
      setIsInitialLoad(false);
      setAllMessages([]); // Clear any existing messages
      loadInitialMessages();
    }

    // Cleanup when group changes
    return () => {
      if (stableGroupId) {
        setAllMessages([]);
        setPageInfo(null);
        setIsLoadingMore(false);
      }
    };
  }, [stableGroupId]);

  const loadInitialMessages = async () => {
    try {
      const result = await loadGroupChatMessages(stableGroupId);

      if (result.success && result.messages.length > 0) {
        // Messages come sorted by createdAt ASC (oldest first)
        // We want to show them in chronological order with newest at bottom
        const sortedMessages = [...result.messages].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );

        setAllMessages(sortedMessages);
        setPageInfo(result.pageInfo);

        // Scroll to bottom after messages load
        setTimeout(() => {
          scrollToBottom(false); // Instant scroll on initial load
          hasScrolledToBottomRef.current = true;
        }, 100);
      } else {
        setAllMessages([]);
        setPageInfo(result.pageInfo);
      }
    } catch (error) {
      console.error('Failed to load initial messages:', error);
      setMutationError('Failed to load messages');
    }
  };

  // HANDLE INCOMING SOCKET MESSAGES - with strict duplicate prevention
  useEffect(() => {
    if (!messages || messages.length === 0 || isInitialLoad) return;

    // Filter out messages that already exist (check by both uuid AND id)
    const newMessages = messages.filter((msg) => {
      const msgIdentifier = msg.uuid || msg.id;
      return !allMessages.some((existing) => {
        const existingIdentifier = existing.uuid || existing.id;
        return existingIdentifier === msgIdentifier;
      });
    });

    if (newMessages.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[GroupChat] Adding new socket messages:', newMessages.length);
      }

      setAllMessages((prev) => {
        // Create a Map to ensure uniqueness by id/uuid
        const messageMap = new Map();

        // Add existing messages first
        prev.forEach((msg) => {
          const key = msg.uuid || msg.id;
          if (key) messageMap.set(key, msg);
        });

        // Add new messages (will overwrite if duplicate)
        newMessages.forEach((msg) => {
          const key = msg.uuid || msg.id;
          if (key) messageMap.set(key, msg);
        });

        // Convert back to array and sort
        const uniqueMessages = Array.from(messageMap.values());
        return uniqueMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });

      // Auto-scroll if near bottom
      setTimeout(() => {
        const container = containerRef.current;
        if (container && !isUserScrollingRef.current) {
          const isNearBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight < 200;

          if (isNearBottom) {
            scrollToBottom(true);
          }
        }
      }, 50);
    }
  }, [messages, allMessages, isInitialLoad]);

  // LOAD OLDER MESSAGES WHEN SCROLLING UP
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Track if user is actively scrolling
    isUserScrollingRef.current = true;
    clearTimeout(window.scrollTimeout);
    window.scrollTimeout = setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 150);

    // Load older messages when scrolling near the top
    if (container.scrollTop < 200 && !isLoadingMore && pageInfo?.hasNextPage) {
      loadOlderMessages();
    }
  }, [isLoadingMore, pageInfo]);

  const loadOlderMessages = async () => {
    if (!stableGroupId || !pageInfo?.endCursor || isLoadingMore) return;

    setIsLoadingMore(true);
    previousScrollHeightRef.current = containerRef.current?.scrollHeight || 0;

    try {
      const result = await loadMoreGroupMessages(stableGroupId, pageInfo.endCursor);
      setPageInfo(result.pageInfo);

      if (result.success && result.messages.length > 0) {
        setAllMessages((prev) => {
          // Create a Map to ensure uniqueness
          const messageMap = new Map();

          // Add existing messages first
          prev.forEach((msg) => {
            const key = msg.uuid || msg.id;
            if (key) messageMap.set(key, msg);
          });

          // Add older messages (won't overwrite if duplicate)
          result.messages.forEach((msg) => {
            const key = msg.uuid || msg.id;
            if (key && !messageMap.has(key)) {
              messageMap.set(key, msg);
            }
          });

          // Convert to array and sort chronologically
          const uniqueMessages = Array.from(messageMap.values());
          return uniqueMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        });

        setPageInfo(result.pageInfo);

        // Restore scroll position after prepending
        setTimeout(() => {
          const container = containerRef.current;
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - previousScrollHeightRef.current;
            container.scrollTop = scrollDiff;
          }
          setIsLoadingMore(false);
        }, 0);
      } else {
        setIsLoadingMore(false);
      }
    } catch (error) {
      console.error('Failed to load more messages:', error);
      setIsLoadingMore(false);
    }
  };

  // Attach scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(window.scrollTimeout);
    };
  }, [handleScroll]);

  const scrollToBottom = useCallback((smooth = true) => {
    const container = containerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }, []);

  // Memoize expensive calculations
  const isInGroupChat = useMemo(
    () => chatState.activeGroupChat?.id === stableGroupId,
    [chatState.activeGroupChat?.id, stableGroupId]
  );

  const isConnected = useMemo(
    () => connectionStatus === 'connected' || connectionStatus === 'connecting',
    [connectionStatus]
  );

  // Handle typing indicators
  const handleInputChange = useCallback(
    (event) => {
      const { value } = event.target;
      setInputMessage(value);

      if (!isTyping && value.trim() && isConnected) {
        setIsTyping(true);
        startTyping('group');
      }

      if (typingTimer) {
        clearTimeout(typingTimer);
      }

      const newTimer = setTimeout(() => {
        setIsTyping(false);
        stopTyping('group');
      }, 3000);

      setTypingTimer(newTimer);
    },
    [isTyping, isConnected, startTyping, stopTyping, typingTimer]
  );

  const handleTypeChange = useCallback((event) => {
    setMessageType(event.target.value);
  }, []);

  // SEND MESSAGE WITH OPTIMISTIC UPDATE
  const handleSendMessage = useCallback(async () => {
    if (!filesPrepared && selectedFiles.length > 0) {
      setMutationError('Please wait for files to be prepared before sending.');
      return;
    }

    const messageText = inputMessage?.trim() || '';
    const selectedFilesArray = [...selectedFiles];

    setInputMessage('');
    setSelectedFiles([]);
    setMutationError(null);

    // Stop typing
    if (isTyping) {
      setIsTyping(false);
      stopTyping('group');
    }
    if (typingTimer) {
      clearTimeout(typingTimer);
      setTypingTimer(null);
    }

    // Scroll to bottom after sending
    setTimeout(() => scrollToBottom(true), 50);

    try {
      // Upload files if any
      const fileURLs = [];
      const errors = [];

      if (selectedFilesArray.length > 0) {
        const uploadPromises = selectedFilesArray.map(async (selectedFile) => {
          const { file, uploadUrl, s3Key } = selectedFile;

          if (!uploadUrl || !file) {
            errors.push(`Missing upload data for ${file?.name || 'file'}`);
            return;
          }

          try {
            const result = await uploadFilesToS3([file], uploadUrl, s3Key);
            if (result.successful?.[0]?.path) {
              fileURLs.push(result.successful[0].path);
            }
            if (result.failed?.[0]?.error) {
              errors.push(result.failed[0].error);
            }
          } catch (err) {
            errors.push(`Upload failed: ${err.message}`);
          }
        });

        await Promise.all(uploadPromises);

        if (errors.length > 0) {
          throw new Error(errors.join('\n'));
        }
      }

      // Send message
      lastMessageSentRef.current = Date.now();

      const sentMessage = await sendGroupMessage({
        groupChatId: stableGroupId,
        message: messageText,
        media_urls: fileURLs,
        type: messageType === 'announcement' ? 'ANNOUNCEMENT' : 'TEXT',
      });

      setSelectedFiles([]);
      setUploadType(null);
      setFilesPrepared(false);

      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    } catch (error) {
      console.error('Send message error:', error);

      // Restore input
      setMutationError(error?.message || 'Failed to send message');
      setInputMessage(messageText);
      setSelectedFiles(selectedFilesArray);
    }
  }, [
    inputMessage,
    selectedFiles,
    filesPrepared,
    isTyping,
    typingTimer,
    messageType,
    stableGroupId,
    stableAuthUserId,
    authUser,
    sendGroupMessage,
    uploadFilesToS3,
    stopTyping,
    scrollToBottom,
  ]);

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  // File upload handlers
  const handleFilesSelected = useCallback(
    async (files) => {
      if (files?.length > 0) {
        // Show previews immediately
        const previewFiles = files.map((file) => ({
          file,
          name: file.name,
          path: URL.createObjectURL(file),
          uploadUrl: null,
          s3Key: null,
        }));
        setSelectedFiles((prev) => [...prev, ...previewFiles]);

        const presignedResult = await generateAwsSignedUrl(files[0]);
        if (presignedResult.success) {
          setMutationError(null);
        } else {
          setMutationError('Failed to get upload URL');
          setFilesPrepared(false);
          return;
        }

        let totalFileSize = 0;
        let hasImageOrVideo = false;
        const filePromises = files.map(async (file) => {
          const isImage = file.type.startsWith('image/');
          const isVideo = file.type.startsWith('video/');
          const fileExtension = file?.name?.split('.').pop()?.toLowerCase();

          if (DISALLOWED_EXTENSIONS.includes(`.${fileExtension}`)) {
            setMutationError(`File ${file?.name} can potentially cause viruses or spyware`);
            setFilesPrepared(false);
            return;
          }

          if (isImage || isVideo) {
            hasImageOrVideo = true;
          } else {
            setUploadType('file');
          }

          let fileToUpload = file;
          if (isImage && file.size > MAX_IMAGE_SIZE_BYTES) {
            const options = {
              maxSizeMB: MAX_IMAGE_SIZE_MB / 2,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            };
            try {
              const compressedFile = await imageCompression(file, options);
              fileToUpload = compressedFile;
            } catch (error) {
              setMutationError(`Error compressing file ${file?.name}:`, error);
              setFilesPrepared(false);
              return;
            }
          }

          totalFileSize += fileToUpload.size;
          if (totalFileSize <= MAX_TOTAL_FILE_SIZE_BYTES) {
            const timestamp = new Date().getTime();
            const fileParts = fileToUpload.name?.split('.');
            const newName = `${fileParts[0]}_${timestamp}.${fileParts[1]}`;
            const renamedFile = new File([fileToUpload], newName, { type: fileToUpload.type });

            const presignedResult = await generateAwsSignedUrl(renamedFile);
            if (!presignedResult.success) {
              setMutationError(`Failed to get an upload URL for ${fileToUpload?.name}`);
              setFilesPrepared(false);
              hasImageOrVideo = false;
            } else {
              const uploadUrl = presignedResult.url;
              const s3Key = presignedResult.key;

              setSelectedFiles((prev) =>
                prev.map((f) =>
                  f.name === file.name
                    ? { ...f, file: renamedFile, name: newName, uploadUrl, s3Key }
                    : f
                )
              );
            }
          } else {
            setMutationError(
              `${file?.name} exceeds the maximum allowed total size of ${MAX_TOTAL_FILE_SIZE_MB}MB`
            );
            setFilesPrepared(false);
            hasImageOrVideo = false;
            totalFileSize -= fileToUpload.size;
          }
        });

        if (hasImageOrVideo) {
          setUploadType('media');
        }

        await Promise.all(filePromises);

        const allFilesPrepared = selectedFiles.every((file) => file.uploadUrl);
        setFilesPrepared(allFilesPrepared);
      } else {
        setFilesPrepared(false);
      }
    },
    [selectedFiles, generateAwsSignedUrl]
  );

  const removeFile = (index) => {
    const updatedFiles = [...selectedFiles];
    const fileToRemove = updatedFiles[index];
    if (fileToRemove?.path?.startsWith('blob:')) {
      URL.revokeObjectURL(fileToRemove.path);
    }
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);

    const allFilesPrepared =
      updatedFiles.length === 0 || updatedFiles.every((file) => file.uploadUrl);
    setFilesPrepared(allFilesPrepared);
  };

  useEffect(() => {
    if (selectedFiles.length === 0) {
      setUploadType(null);
    }
  }, [selectedFiles.length]);

  const getUserRole = useCallback(
    (userId) => {
      const member = group.members?.find((m) => m.user?.id === userId);
      return member?.role || 'MEMBER';
    },
    [group.members]
  );

  const isUserOnline = useCallback(
    (userId) => {
      const member = chatState.groupChatMembers?.find((m) => m.user?.id === userId);
      return member?.lastReadAt
        ? new Date(member.lastReadAt).getTime() > Date.now() - 300000
        : false;
    },
    [chatState.groupChatMembers]
  );

  const typingUsers = useMemo(() => {
    const typingKey = `group_${stableGroupId}`;
    const typingData = chatState.typingUsers[typingKey];

    if (!typingData || !typingData.isTyping) return [];

    return (
      group.members?.filter(
        (m) => m.user?.id !== stableAuthUserId && typingData.userIds?.includes(m.user?.id)
      ) || []
    );
  }, [stableGroupId, chatState.typingUsers, group.members, stableAuthUserId]);

  const toggleParticipants = useCallback(() => {
    setShowParticipants((prev) => !prev);
  }, []);

  // RENDER MESSAGES
  const renderMessagesContent = () => {
    if (isInitialLoad) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress />
        </Box>
      );
    }

    if (allMessages.length === 0) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          flexDirection="column"
        >
          <Typography variant="body2" color="text.secondary">
            No messages yet. Start the conversation!
          </Typography>
        </Box>
      );
    }

    // Debug: Check for duplicates
    const messageIds = allMessages.map((m) => m.uuid || m.id);
    const uniqueIds = new Set(messageIds);
    if (messageIds.length !== uniqueIds.size) {
      console.error('🚨 DUPLICATE MESSAGE IDS DETECTED:', {
        total: messageIds.length,
        unique: uniqueIds.size,
        duplicates: messageIds.filter((id, index) => messageIds.indexOf(id) !== index),
      });
    }

    return (
      <List
        sx={{
          p: 2,
          overflowY: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        ref={containerRef}
      >
        {/* Loading older messages indicator */}
        {isLoadingMore && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* No more messages indicator */}
        {!pageInfo?.hasNextPage && allMessages.length > 15 && (
          <Typography variant="caption" color="text.secondary" align="center" sx={{ py: 1 }}>
            No more messages
          </Typography>
        )}

        {/* Messages - render in chronological order with guaranteed unique keys */}
        {allMessages.map((msg, index) => {
          // Create a guaranteed unique key
          const uniqueKey = msg.uuid || msg.id || `msg-${index}-${msg.createdAt}`;

          return (
            <MessageItem
              key={uniqueKey}
              msg={msg}
              index={index}
              stableAuthUserId={stableAuthUserId}
              chatState={chatState}
              theme={theme}
              getUserRole={getUserRole}
              isUserOnline={isUserOnline}
              expandedMedia={expandedMedia}
              setExpandedMedia={setExpandedMedia}
            />
          );
        })}

        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              {typingUsers.map((u) => u.user?.name).join(', ')}
              {typingUsers.length === 1 ? ' is' : ' are'} typing...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </List>
    );
  };

  if (error) {
    return (
      <Paper sx={{ p: 2, m: 2 }}>
        <Alert severity="error" onClose={onClose}>
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100vh',
        minHeight: '400px',
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Event Discussion
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {eventTitle}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Show participants">
            <IconButton
              onClick={toggleParticipants}
              color={showParticipants ? 'primary' : 'default'}
            >
              <Badge badgeContent={group.members?.length || 0} color="primary">
                <PeopleIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Chip
            label={connectionStatus}
            size="small"
            color={isConnected ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Participants panel */}
      {showParticipants && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', maxHeight: '150px', overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ p: 1, bgcolor: 'background.default' }}>
            Participants ({group.members?.length || 0})
          </Typography>

          <List dense sx={{ p: 0 }}>
            {group.members?.map((member) => (
              <ListItem key={member.id}>
                <ListItemAvatar>
                  <Badge variant="dot" color="success" invisible={!isUserOnline(member.id)}>
                    <Avatar src={member.avatar} sx={{ width: 24, height: 24 }}>
                      {member.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>

                <ListItemText
                  primary={member.name}
                  secondary={member.role}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>{renderMessagesContent()}</Box>

      {/* File previews */}
      {selectedFiles.length > 0 && (
        <Box
          sx={{ p: 2, borderTop: 1, borderColor: 'divider', maxHeight: '200px', overflowY: 'auto' }}
        >
          <Grid container spacing={1}>
            {selectedFiles?.map((file, index) => (
              <Grid item key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                  {file.file.type.startsWith('image/') ? (
                    <Box
                      sx={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '5px',
                        overflow: 'hidden',
                        border: '2px solid #F35251',
                        position: 'relative',
                      }}
                    >
                      <Image
                        src={file.path}
                        alt={file?.name}
                        height="100%"
                        width="100%"
                        style={{ objectFit: 'cover' }}
                      />
                    </Box>
                  ) : file.file.type.startsWith('video/') ? (
                    <Box
                      sx={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '5px',
                        overflow: 'hidden',
                        border: '2px solid #F35251',
                      }}
                    >
                      <video style={{ width: '100%', height: '100%', objectFit: 'cover' }}>
                        <source src={file.path} type={file.file.type} />
                      </video>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 1,
                        border: '2px solid #F35251',
                        borderRadius: '5px',
                        maxWidth: '200px',
                      }}
                    >
                      <Typography variant="caption" noWrap>
                        {removeTimeStampFromFile(decodeURIComponent(file?.name))}
                      </Typography>
                    </Box>
                  )}
                  <IconButton
                    onClick={() => removeFile(index)}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: '#F35251',
                      color: 'white',
                      '&:hover': {
                        bgcolor: '#D03A3A',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Message input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {mutationError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMutationError(null)}>
            {mutationError}
          </Alert>
        )}
        <Box display="flex" gap={1}>
          <Select
            value={messageType}
            onChange={handleTypeChange}
            size="small"
            sx={{ minWidth: 120 }}
            variant="outlined"
          >
            <MuiMenuItem value="general">General</MuiMenuItem>
            <MuiMenuItem value="announcement">Announcement</MuiMenuItem>
          </Select>

          <TextField
            ref={messageInputRef}
            fullWidth
            multiline
            maxRows={3}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FileInput
                    onFilesSelected={handleFilesSelected}
                    uploadType={uploadType}
                    setUploadType={setUploadType}
                    buttonProps={{
                      'aria-label': 'file upload',
                      disabled: false,
                      style: {
                        color: 'white',
                        backgroundColor: theme.palette.primary.main,
                        padding: 8,
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleSendMessage}
                    color="primary"
                    disabled={!inputMessage.trim() && selectedFiles.length === 0}
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

EventDiscussionPanel.propTypes = {
  eventTitle: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  group: PropTypes.object.isRequired,
  messages: PropTypes.array,
};

EventDiscussionPanel.defaultProps = {
  onClose: () => {},
  messages: [],
};

export default EventDiscussionPanel;
